// §7 SceneFallback — the habitat-keyed water panel used wherever a real
// photo is missing / failing / low-res. Fallbacks must look intentional
// (never a gray box, never a broken image, never a letterbox), so this
// composes a water ramp + god rays + caustics + vignette + optional
// species silhouette.
//
// Three ramps carry meaning: lagoon (shallow reef), reef (mid), deep
// (open water / big pelagics). Callers pass `variant` — otherwise a
// deterministic hash of the scientific name picks one so retries render
// the same scene.
//
// The rays + caustics recipes are the two `.g-rays` / `.g-caustics`
// helper classes in `index.css`; nothing else in the app should hand-roll
// caustic layers.

import type { ReactNode } from "react";
import {
  TurtleSil,
  MantaSil,
  Fish1Sil,
  Fish2Sil,
  DolphinSil,
} from "./Icons";

export type SceneVariant = "lagoon" | "reef" | "deep";

// Ramp names map 1:1 to Tailwind `bg-scene-*` utilities defined in the
// tailwind.config.ts backgroundImage entries — that's where the hex
// values actually live, so §10's hex gate stays clean.
const RAMP_CLASS: Record<SceneVariant, string> = {
  lagoon: "bg-scene-lagoon",
  reef: "bg-scene-reef",
  deep: "bg-scene-deep",
};

type Silhouette = "turtle" | "manta" | "dolphin" | "fish1" | "fish2" | "none";

// Genus prefixes for cetaceans — dolphins, porpoises, toothed / baleen
// whales. iNat/GBIF/OBIS sometimes surface these with no photo (default
// photos on iNat are typically UGC snorkeler shots), so the fallback
// matters more than for reef fish. Prefix match keeps the check cheap
// and stays correct even when the entry only has "Genus species".
const CETACEAN_GENERA = [
  // Dolphinidae — oceanic dolphins
  "stenella", "tursiops", "delphinus", "orcinus", "globicephala",
  "grampus", "steno", "pseudorca", "feresa", "peponocephala",
  "sotalia", "sousa", "lissodelphis", "lagenorhynchus",
  "lagenodelphis", "cephalorhynchus",
  // Phocoenidae — porpoises
  "neophocaena", "phocoena", "phocoenoides",
  // Toothed whales
  "kogia", "physeter",
  // Baleen whales
  "balaenoptera", "megaptera", "balaena", "eubalaena", "eschrichtius",
  // Beaked whales
  "berardius", "hyperoodon", "ziphius", "mesoplodon",
  // Arctic whales
  "delphinapterus", "monodon",
];

// Deterministic silhouette pick from the scientific name so the same
// species always renders with the same fauna even across re-mounts.
function silhouetteFor(hint?: string): Silhouette {
  if (!hint) return "fish1";
  const key = hint.toLowerCase();
  if (
    key.startsWith("chelonia") ||
    key.startsWith("eretmochelys") ||
    key.startsWith("caretta") ||
    key.startsWith("lepidochelys") ||
    key.startsWith("natator") ||
    key.startsWith("dermochelys")
  ) {
    return "turtle";
  }
  if (
    key.startsWith("mobula") ||
    key.startsWith("manta") ||
    key.startsWith("aetobatus") ||
    key.startsWith("rhincodon")
  ) {
    return "manta";
  }
  if (CETACEAN_GENERA.some((g) => key.startsWith(g))) {
    return "dolphin";
  }
  // Consistent split across the remaining names.
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return Math.abs(h) % 2 === 0 ? "fish1" : "fish2";
}

function variantFor(hint?: string): SceneVariant {
  if (!hint) return "lagoon";
  const key = hint.toLowerCase();
  const sil = silhouetteFor(key);
  if (sil === "manta" || sil === "dolphin") return "deep";
  if (sil === "turtle") return "lagoon";
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 17 + key.charCodeAt(i)) | 0;
  const bucket = Math.abs(h) % 3;
  return bucket === 0 ? "lagoon" : bucket === 1 ? "reef" : "deep";
}

function SilRender({ kind, className }: { kind: Silhouette; className?: string }) {
  if (kind === "none") return null;
  const props = { className };
  if (kind === "turtle") return <TurtleSil {...props} />;
  if (kind === "manta") return <MantaSil {...props} />;
  if (kind === "dolphin") return <DolphinSil {...props} />;
  if (kind === "fish1") return <Fish1Sil {...props} />;
  return <Fish2Sil {...props} />;
}

export interface SceneFallbackProps {
  /** Scientific name (drives silhouette + variant selection). */
  hint?: string;
  /** Manual variant override. */
  variant?: SceneVariant;
  /** Manual silhouette override; "none" hides it entirely. */
  silhouette?: Silhouette;
  /** Extra classes on the outer scene wrapper. */
  className?: string;
  /** Silhouette size + position (Tailwind classes, e.g. "w-14 left-3 top-2"). */
  silhouetteClass?: string;
  /** Optional overlay children (e.g. a badge, a label). */
  children?: ReactNode;
  /** Turn off vignette when the scene is a small thumbnail. */
  vignette?: boolean;
  /**
   * List / small-thumb mode: skip the god-rays layer and downgrade the
   * caustic recipe to a single lightweight radial. Cheaper on the GPU
   * during scroll — Home in Hawaii regularly stacks 8–14 photoless species
   * above the fold, and the full recipe means the compositor runs 16–28
   * mix-blend-mode: screen passes per frame. Use for row / mini thumbs;
   * leave off for hero-sized scenes where the full caustic is the point.
   */
  simple?: boolean;
}

export function SceneFallback({
  hint,
  variant,
  silhouette,
  className = "",
  silhouetteClass = "w-1/2 left-1/4 top-[15%]",
  children,
  vignette = true,
  simple = false,
}: SceneFallbackProps) {
  const v = variant ?? variantFor(hint);
  const sil = silhouette ?? silhouetteFor(hint);
  return (
    <div
      className={`relative overflow-hidden ${RAMP_CLASS[v]} ${className}`}
      aria-hidden={!hint}
    >
      {simple ? (
        // One inexpensive radial to keep some habitat-light character
        // without the two blur+screen passes.
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 40% at 26% 26%, rgba(255,255,255,0.22), transparent 70%)",
          }}
        />
      ) : (
        <>
          <div className="g-rays" />
          <div className="g-caustics" />
        </>
      )}
      {vignette && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(130% 95% at 50% 28%, transparent 52%, rgba(3,21,29,0.5))",
          }}
        />
      )}
      <SilRender
        kind={sil}
        className={`pointer-events-none absolute text-abyss ${silhouetteClass}`}
      />
      {children}
    </div>
  );
}
